import React, { useEffect, useState } from 'react';
import { Alert, Button, Card, Form } from 'react-bootstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { apiPost } from '../../services/api';

export default function TwoFactorSetup() {
  const { token, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const setupState = location.state || {};
  const preAuthToken = setupState.preAuthToken || token;

  const [secret, setSecret] = useState('');
  const [qrUrl, setQrUrl] = useState('');
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!preAuthToken) {
      navigate('/login', { replace: true });
      return;
    }

    let cancelled = false;

    (async () => {
      setLoading(true);
      setError('');
      try {
        const res = await apiPost('/api/v1/auth/2fa/setup', {}, preAuthToken);
        const data = await res.json().catch(() => ({}));

        if (!res.ok) {
          if (!cancelled) {
            setError(data.message || 'Unable to start two-factor setup.');
          }
          return;
        }

        if (!cancelled) {
          setSecret(data?.data?.secret || '');
          setQrUrl(data?.data?.qr_url || '');
        }
      } catch {
        if (!cancelled) {
          setError('Unable to start two-factor setup.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [preAuthToken, navigate]);

  async function handleConfirm(e) {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    try {
      const res = await apiPost('/api/v1/auth/2fa/confirm', { secret, code }, preAuthToken);
      const data = await res.json().catch(() => ({}));

      if (res.status === 422) {
        setError('Invalid or expired code');
        return;
      }

      if (!res.ok) {
        setError(data.message || 'Unable to enable two-factor authentication.');
        return;
      }

      const u = data?.data?.user;
      const t = data?.data?.token;
      if (u && t) {
        login(u, t);
      }

      navigate('/admin', {
        replace: true,
        state: { twoFactorEnabled: true },
      });
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <div className="rwm-auth bg-white">
        <div className="rwm-auth__card py-5 text-center text-muted">
          <span>Loading…</span>
        </div>
      </div>
    );
  }

  return (
    <div className="rwm-auth bg-white">
      <div className="rwm-auth__card py-5">
        <Card className="border" style={{ borderColor: 'var(--rwm-border)' }}>
          <Card.Body className="p-4">
            <h1 className="h4 fw-semibold mb-3" style={{ color: 'var(--rwm-dark)' }}>
              Set up Two-Factor Authentication
            </h1>
            <p className="text-muted mb-4">
              Scan this QR code with Google Authenticator or Authy.
            </p>

            {error ? (
              <Alert variant="danger" className="mb-4" data-testid="rwm-2fa-error">
                {error}
              </Alert>
            ) : null}

            {qrUrl ? (
              <div className="text-center mb-4">
                <img
                  src={qrUrl}
                  alt="Two-factor authentication QR code"
                  className="img-fluid"
                  data-testid="rwm-2fa-qr-code"
                />
              </div>
            ) : null}

            {secret ? (
              <p className="small text-muted mb-4" data-testid="rwm-2fa-secret-text">
                Manual entry key: <strong>{secret}</strong>
              </p>
            ) : null}

            <p className="mb-3">
              Enter the 6-digit code from your authenticator app to confirm.
            </p>

            <Form onSubmit={handleConfirm}>
              <Form.Group className="mb-3" controlId="twoFactorConfirmCode">
                <Form.Label>Authentication code</Form.Label>
                <Form.Control
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]{6}"
                  maxLength={6}
                  value={code}
                  onChange={(ev) => setCode(ev.target.value.replace(/\D/g, '').slice(0, 6))}
                  required
                  data-testid="rwm-2fa-confirm-code"
                />
              </Form.Group>
              <Button
                type="submit"
                variant="info"
                className="w-100 text-white btn-lg"
                disabled={submitting || code.length !== 6}
                data-testid="rwm-2fa-confirm-submit"
              >
                {submitting ? 'Confirming…' : 'Confirm and continue'}
              </Button>
            </Form>
          </Card.Body>
        </Card>
      </div>
    </div>
  );
}
